import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setAssignedContacts,
  updateAssignedContact,
  addAssignedContact,
  removeAssignedContact,
  setContactOptions,
  setContactsLoading,
} from "../../../../../redux/buildings/buildingContactsSlice";
import {
  fetchAssignedContacts,
  fetchAllContacts,
  updateContact,
  updateAssignedContacts as updateAssignedBuildingContactsService,
} from "../services/buildingContactService";

const useBuildingContacts = (buildingId) => {
  const dispatch = useDispatch();
  const assignedContacts =
    useSelector((state) => state.buildingContacts.assignedContacts) || [];
  const contactOptions =
    useSelector((state) => state.buildingContacts.contactOptions) || [];
  const loading = useSelector((state) => state.buildingContacts.loading);

  const loadContacts = useCallback(async () => {
    if (!buildingId) return;
    dispatch(setContactsLoading(true));
    try {
      const [allContacts, assigned] = await Promise.all([
        fetchAllContacts(),
        fetchAssignedContacts(buildingId),
      ]);
      dispatch(
        setContactOptions(Array.isArray(allContacts) ? allContacts : [])
      );
      // Filter assigned contacts based on the three roles.
      const filteredAssigned = Array.isArray(assigned)
        ? assigned.filter(
            (item) =>
              item.mainContact ||
              item.agentContact ||
              item.superintendentContact ||
              item.collectionsContact ||
              item.dobContact
          )
        : [];

      const enrichedAssigned = filteredAssigned.map((assignedContact) => {
        const fullContact = allContacts.find(
          (c) => c.contactID === assignedContact.contactId
        );
        return fullContact
          ? {
              ...fullContact,
              ...assignedContact,
              contactID: assignedContact.contactId,
            }
          : assignedContact;
      });
      dispatch(setAssignedContacts(enrichedAssigned));
    } catch (error) {
      console.error("Error loading building contacts:", error);
      dispatch(setAssignedContacts([]));
    } finally {
      dispatch(setContactsLoading(false));
    }
  }, [dispatch, buildingId]);

  useEffect(() => {
    if (buildingId) loadContacts();
  }, [buildingId, loadContacts]);

  const handleRowFieldChange = useCallback(
    (contactID, field, value) => {
      dispatch(
        updateAssignedContact(contactID, {
          [field]: value,
        })
      );
    },
    [dispatch]
  );

  const handleRowAutoSave = useCallback(
    async (contactID) => {
      const contact = assignedContacts.find((c) => c.contactID === contactID);
      if (!contact || !contact.contactID) return;
      try {
        await updateContact(contactID, contact);
      } catch (error) {
        console.error("Error updating contact:", error);
      }
    },
    [assignedContacts]
  );

  const addContactForRole = useCallback(
    async (roleKey, contactID) => {
      if (!contactID) {
        alert("Please select a contact to add.");
        return;
      }
      const selected = contactOptions.find(
        (c) => c.contactID.toString() === contactID.toString()
      );
      if (!selected) {
        console.error("Selected building contact not found in options");
        return;
      }
      const existingContact = assignedContacts.find(
        (c) => c.contactID.toString() === contactID.toString()
      );
      if (existingContact) {
        if (existingContact[roleKey]) {
          alert(`Contact is already assigned as ${roleKey}.`);
          return;
        } else {
          const updatedContact = { ...existingContact, [roleKey]: true };
          dispatch(
            updateAssignedContact({
              contactID,
              updates: { [roleKey]: true },
            })
          );
          const updatedContacts = assignedContacts.map((c) =>
            c.contactID.toString() === contactID.toString() ? updatedContact : c
          );
          try {
            await updateAssignedBuildingContactsService(
              buildingId,
              updatedContacts
            );
          } catch (error) {
            console.error(
              "Error updating assigned building contacts on server:",
              error
            );
          }
          return;
        }
      }
      const newContact = {
        ...selected,
        buildingId,
        mainContact: roleKey === "mainContact",
        agentContact: roleKey === "agentContact",
        superintendentContact: roleKey === "superintendentContact",
        collectionsContact: roleKey === "collectionsContact",
        dobContact: roleKey === "dobContact",
        isEditing: false,
        roleKey,
      };
      dispatch(addAssignedContact(newContact));
      try {
        await updateAssignedBuildingContactsService(buildingId, [
          ...assignedContacts,
          newContact,
        ]);
      } catch (error) {
        console.error(
          "Error updating assigned building contacts on server:",
          error
        );
      }
    },
    [contactOptions, assignedContacts, buildingId, dispatch]
  );

  const removeContactForRole = useCallback(
    async (roleKey, contactID) => {
      const existingContact = assignedContacts.find(
        (c) => c.contactID.toString() === contactID.toString()
      );
      if (!existingContact) {
        console.error("Contact not found in assigned contacts");
        return;
      }
      const updatedContact = { ...existingContact, [roleKey]: false };
      dispatch(
        updateAssignedContact({
          contactID,
          updates: { [roleKey]: false },
        })
      );
      const updatedContacts = assignedContacts.map((c) =>
        c.contactID.toString() === contactID.toString() ? updatedContact : c
      );
      try {
        await updateAssignedBuildingContactsService(
          buildingId,
          updatedContacts
        );
      } catch (error) {
        console.error(
          "Error updating assigned building contacts on server:",
          error
        );
      }
    },
    [assignedContacts, buildingId, dispatch]
  );

  return {
    assignedContacts,
    contactOptions,
    loading,
    handleRowFieldChange,
    handleRowAutoSave,
    addContactForRole,
    removeContactForRole,
  };
};

export default useBuildingContacts;
